import {Component, memo, createRef} from 'react';
import {
  DataGrid,
  Column,
  Editing,
  Lookup,
  MasterDetail,
} from 'devextreme-react/data-grid';
import { TagBox } from 'devextreme-react/tag-box';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import SelectBox from 'devextreme-react/select-box';
import { CreateAuthorizedDataSource, SetAuthorizationHeaderAsync } from '~/helpers/data';
import { appConst } from '~/app-const';
import { CommitteeMembershipRoleDetails } from '~/pages/committees/committee-membership-role-details';
import { calcGridHeight, setDateInlineEditing } from '~/helpers/ui';

const committeeMembershipUrl = `${window.env.apiEndpoint}/api/v1/CommitteeMemberships`;

const committeesData = CreateAuthorizedDataSource(
  { loadUrl: `${committeeMembershipUrl}/CommitteesLookup` },
  null,
  'Value',
  null,
  'Header',
);
const schoolYearsData = CreateAuthorizedDataSource(
  { loadUrl: `${committeeMembershipUrl}/SchoolYearsLookup` },
  null,
  'Value', false,
);

function getFilteredCommitteeRoleData(committeeId) {
  let store = CreateAuthorizedDataSource(
    { loadUrl: `${committeeMembershipUrl}/GetRoleLookupsForCommittee` },
    { load: { committeeId: committeeId } },
    'Value', false,
  );
  return store;
};

class CommitteeGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      committeeId: 0
    };
    this.selectBoxCommitteeColumnTemplate = this.selectBoxCommitteeColumnTemplate.bind(this);
    this.selectBoxSchoolYearColumnTemplate = this.selectBoxSchoolYearColumnTemplate.bind(this);
    this.committeeMembershipDataSourceStore = this.committeeMembershipDataSourceStore.bind(this);
    this.tagBoxCommitteeRoleNameColumnTemplate = this.tagBoxCommitteeRoleNameColumnTemplate.bind(this);
    this.onTagBoxCommitteeRoleNameValueChanged = this.onTagBoxCommitteeRoleNameValueChanged.bind(this);

    this.committeeMembershipsGridRef = createRef();
  }

  committeeMembershipDataSourceStore() {
    return createStore(
      {
        insertUrl: `${committeeMembershipUrl}/AddPersonToCommittee`,
        loadUrl: `${committeeMembershipUrl}/GetAssociatedCommitteesByPerson`,
        deleteUrl: `${committeeMembershipUrl}/Delete`,
        updateUrl: `${committeeMembershipUrl}/UpdateCommitteeMembership`,
        key: 'MembershipId',
        onBeforeSend: async (method, ajaxOptions) => {
          await SetAuthorizationHeaderAsync(ajaxOptions);
          const { personId } = this.props;
          if (method === 'insert') {
            const {
              committeeId,
              schoolYearId,
              rolesSelected,
            } = this.state;
            const json = JSON.parse(ajaxOptions.data.values);
            ajaxOptions.data.personId = personId;
            ajaxOptions.data.committeeId = committeeId;
            ajaxOptions.data.schoolYearId = schoolYearId;
            ajaxOptions.data.startDate = json.CommitteeMembershipStartDate;
            ajaxOptions.data.endDate = json.CommitteeMembershipEndDate;
            ajaxOptions.data.roleTexts = rolesSelected && rolesSelected.join();
          }
          if (method === 'load') {
            ajaxOptions.data.personId = personId;
          }
        },
        onUpdated: () => {
          const { refreshGrid } = this.props;
          refreshGrid();
        },
      },
    );
  }

  setGridHeight() {
    this.committeeMembershipsGridRef.current.instance().option('height', calcGridHeight('committeeMembershipGrid'));
  }

  onEditorPreparingCommitteeMembershipsGrid(e) {
    setDateInlineEditing(['CommitteeMembershipStartDate', 'CommitteeMembershipEndDate'], e);
    if (e.dataField === "CommitteeRoleId" && e.row.rowType === "data") {
      if (e.row.isNewRow) {
        e.editorOptions.disabled = false;
      } else {
        e.editorOptions.disabled = true;
      }
    }
  }  

  onSelectBoxCommitteeValueChanged(cell, e) {
    cell.setValue(e.value);
    this.setState({
      committeeId: e.value,
    });
  }

  selectBoxCommitteeColumnTemplate(cell) {
    const onValueChanged = this.onSelectBoxCommitteeValueChanged.bind(this, cell);
    return (
      <SelectBox
        {...appConst.defaultSelectBoxOptions}
        defaultValue={cell.value}
        {...cell.column.lookup}
        onValueChanged={onValueChanged}
        itemRender={this.itemRender}
        grouped
        dataSource={committeesData}
        disabled={typeof cell.row.isNewRow === 'undefined'}
      />
    );
  }

  onSelectBoxSchoolYearValueChanged(cell, e) {
    cell.setValue(e.value);
    this.setState({
      schoolYearId: e.value,
    });
    const items = e.component.getDataSource().items();
    const item = items.find((item) => item.Value === e.value);
    if (item) {
      const i1 = cell.row.cells.find((c) => c.column.dataField === 'CommitteeMembershipStartDate').columnIndex;
      const i2 = cell.row.cells.find((c) => c.column.dataField === 'CommitteeMembershipEndDate').columnIndex;

      cell.row.cells[i1].setValue(item.StartDate);
      cell.row.cells[i2].setValue(item.EndDate);
    }
  }

  selectBoxSchoolYearColumnTemplate(cell) {
    const onValueChanged = this.onSelectBoxSchoolYearValueChanged.bind(this, cell);
    return (
      <SelectBox
        {...appConst.defaultSelectBoxOptions}
        defaultValue={cell.value}
        {...cell.column.lookup}
        onValueChanged={onValueChanged}
        itemRender={this.itemRender}
        grouped={false}
        dataSource={schoolYearsData}
        disabled={typeof cell.row.isNewRow === 'undefined'}
      />
    );
  }

  onTagBoxCommitteeRoleNameValueChanged(cell, e) {
    cell.setValue(e.value);
    this.setState({ rolesSelected: e.value })
  }

  tagBoxCommitteeRoleNameColumnTemplate(cell) {
    const onValueChanged = this.onTagBoxCommitteeRoleNameValueChanged.bind(this, cell);
    return (
      <TagBox
        dataSource={getFilteredCommitteeRoleData(this.state.committeeId)}
        valueExpr="Text"
        displayExpr="Text"
        onValueChanged={onValueChanged}
        value={this.state.rolesSelected}
      />
    );
  }

  render() {
    return (
      <DataGrid
        id="committeeMembershipGrid"
        ref={this.committeeMembershipsGridRef}
        dataSource={this.committeeMembershipDataSourceStore()}
        {...appConst.defaultGridOptions}
        onRowInserted={
          () => {
            this.setState({ rolesSelected: null })
          }
        }
        onEditorPreparing={this.onEditorPreparingCommitteeMembershipsGrid}
      >
        <Editing
          mode="row"
          allowAdding
          allowDeleting
          allowUpdating={false}
        />
        <Column
          dataField="CommitteeId"
          dataType="number"
          caption="Committee"
          editCellRender={this.selectBoxCommitteeColumnTemplate}
        >
          <Lookup
            dataSource={committeesData.store()}
            valueExpr="Value"
            displayExpr="Text"
          />
        </Column>
        <Column
          dataField="CommitteeRoleName"
          dataType="text"
          caption="Roles"
          editCellRender={this.tagBoxCommitteeRoleNameColumnTemplate}
        />
        <Column
          dataField="SchoolYearId"
          dataType="number"
          caption="School Year"
          editCellRender={this.selectBoxSchoolYearColumnTemplate}
        >
          <Lookup
            dataSource={schoolYearsData.store()}
            valueExpr="Value"
            displayExpr="Text"
          />
        </Column>
        <Column
          dataField="CommitteeMembershipStartDate"
          dataType="date"
          caption="Start Date"
          format={appConst.dateDisplayFormat}
        />
        <Column
          dataField="CommitteeMembershipEndDate"
          dataType="date"
          caption="End Date"
          format={appConst.dateDisplayFormat}
        />
        <MasterDetail
          enabled={true}
          render={props => {
            return (
              <CommitteeMembershipRoleDetails
                committeeMembershipId={props.key}
                committeeId={props.data.CommitteeId}
                refreshMasterGrid={() => {
                  this.committeeMembershipsGridRef.current.instance().refresh()
                }}
              />
            );
          }}
        />
      </DataGrid>
    );
  }
}

const CommitteeGridMemo = memo(CommitteeGrid);
export {CommitteeGridMemo as CommitteeGrid};
